// src/components/admin/CategoryDialog.jsx
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { translations } from '../../utils/translations';
import { shopSettingsApi } from '../../api';

// 额外语言字段配置——和 ProductDialog.jsx、ShopNameCard.jsx 用的是同一套
// 语言代码/标签，保持整个系统"哪个代码对应哪种语言"这件事统一
const LANGUAGE_FIELD_CONFIG = [
  { code: 'en',    field: 'name_en',    label: '英文',   placeholder: '例：Beverages' },
  { code: 'km',    field: 'name_km',    label: '高棉语', placeholder: '例：ភេសជ្ជៈ' },
  { code: 'ms',    field: 'name_ms',    label: '马来语', placeholder: '例：Minuman' },
  { code: 'zh_tw', field: 'name_zh_tw', label: '繁体中文', placeholder: '例：飲料類' },
];

const CategoryDialog = ({ category, parentId, parentCategories = [], onClose, onSubmit }) => {
  const { language } = useApp();
  const t = translations[language];

  const [formData, setFormData] = useState({
    name: category?.name || '',
    name_en: category?.name_en || '',
    name_km: category?.name_km || '',
    name_ms: category?.name_ms || '',
    name_zh_tw: category?.name_zh_tw || '',
    parent_id: category?.parent_id || parentId || '',
    sort_order: category?.sort_order || 0
  });

  // 额外语言输入框：只显示商家设置里"启用的语言"里有的那几个，
  // 和 ProductDialog.jsx 用的是同一套逻辑
  const [enabledLangCodes, setEnabledLangCodes] = useState(['en', 'km']); // 加载完设置前的默认兜底
  useEffect(() => {
    shopSettingsApi.getSettings().then(res => {
      if (res.success && res.data && Array.isArray(res.data.enabled_languages)) {
        setEnabledLangCodes(res.data.enabled_languages.filter(c => c !== 'zh'));
      }
    }).catch(() => {}); // 加载失败就用上面的默认兜底，不影响表单基本可用
  }, []);
  const visibleLangFields = LANGUAGE_FIELD_CONFIG.filter(l => enabledLangCodes.includes(l.code));

  const isEdit = !!category;
  // 编辑一级分类时不显示父级选择
  const isParentCategory = isEdit && category.level === 1;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      parent_id: formData.parent_id || null,
      sort_order: parseInt(formData.sort_order) || 0
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md">
        <div className="border-b p-6 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold">
              {isEdit ? '编辑分类' : (parentId ? '新增子分类' : '新增大类')}
            </h3>
            {parentId && !isEdit && (
              <p className="text-sm text-gray-500 mt-0.5">
                父级：{parentCategories.find(p => p.id === parentId)?.name || ''}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">

          {/* 父级分类（新增时可选，编辑一级时隐藏） */}
          {!isParentCategory && (
            <div>
              <label className="block text-sm font-medium mb-1">
                所属大类
                <span className="text-gray-400 text-xs ml-1">（不选则为大类）</span>
              </label>
              <select
                value={formData.parent_id}
                onChange={e => setFormData({ ...formData, parent_id: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— 作为独立大类 —</option>
                {parentCategories.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* 中文名——永远显示 */}
          <div>
            <label className="block text-sm font-medium mb-1">分类名称（中文）*</label>
            <input type="text" value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="例：飲料類" required />
          </div>

          {/* 额外语言输入框：只显示商家设置里启用了的那几个语言 */}
          {visibleLangFields.map(lang => (
            <div key={lang.code}>
              <label className="block text-sm font-medium mb-1">分类名称（{lang.label}）</label>
              <input type="text" value={formData[lang.field]}
                onChange={e => setFormData({ ...formData, [lang.field]: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder={lang.placeholder} />
            </div>
          ))}

          {/* 排序 */}
          <div>
            <label className="block text-sm font-medium mb-1">
              排序
              <span className="text-gray-400 text-xs ml-1">（数字越小越靠前）</span>
            </label>
            <input type="number" min="0" value={formData.sort_order}
              onChange={e => setFormData({ ...formData, sort_order: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              取消
            </button>
            <button type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              {isEdit ? '保存' : '确认添加'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryDialog;